import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Inbox, RefreshCcw, Mail, Eye, MousePointerClick, AlertTriangle, XCircle,
  CheckCircle2, Loader2, Filter, Clock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import AdminLayout from '@/components/admin/AdminLayout';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  { value: 'all',    label: 'All' },
  { value: 'sent',   label: 'Sent' },
  { value: 'failed', label: 'Failed' },
];

function relativeTime(iso) {
  if (!iso) return '—';
  try {
    const dt = new Date(iso);
    const diff = Math.floor((Date.now() - dt.getTime()) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
    return dt.toLocaleDateString();
  } catch { return '—'; }
}

function AdminOutreachLog() {
  const [data, setData] = useState({
    items: [], total: 0, sent: 0, failed: 0, opened: 0, clicked: 0, bounced: 0,
    open_rate: 0, click_rate: 0,
  });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/admin/outreach-log`, {
        params: { status: statusFilter, limit: 200 },
        headers: { Authorization: `Bearer ${token}` },
      });
      setData({
        items: res.data?.items || [],
        total: res.data?.total || 0,
        sent: res.data?.sent || 0,
        failed: res.data?.failed || 0,
        opened: res.data?.opened || 0,
        clicked: res.data?.clicked || 0,
        bounced: res.data?.bounced || 0,
        open_rate: res.data?.open_rate || 0,
        click_rate: res.data?.click_rate || 0,
      });
    } catch {
      toast.error('Failed to load outreach log');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data.items;
    return data.items.filter((it) =>
      (it.partner_name || '').toLowerCase().includes(q) ||
      (it.to_email || '').toLowerCase().includes(q) ||
      (it.subject || '').toLowerCase().includes(q),
    );
  }, [data.items, search]);

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-outreach-log-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Inbox className="h-6 w-6 text-[#00F0FF]" />
              <h1 className="text-2xl font-bold text-white">Outreach Log</h1>
            </div>
            <p className="text-sm text-white/60 max-w-2xl">
              Every Featured Platform Partner pitch sent through{' '}
              <code className="text-[#00F0FF] text-xs">/admin/upgrade-candidates</code>, with live open/click data
              from Resend's webhook. Use this to decide who to follow up with at 7+ days.
            </p>
          </div>
          <Button
            variant="outline" size="sm" onClick={load} disabled={loading}
            data-testid="outreach-log-refresh-btn"
            className="border-white/20 text-white hover:bg-white/10"
          >
            <RefreshCcw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <Stat label="Total sent" value={data.sent} icon={Mail} color="text-white" testid="stat-sent" />
          <Stat label="Failed" value={data.failed} icon={XCircle} color="text-red-300" testid="stat-failed" />
          <Stat label="Opened" value={data.opened} icon={Eye} color="text-[#00F0FF]" testid="stat-opened" />
          <Stat label="Clicked" value={data.clicked} icon={MousePointerClick} color="text-[#00FF94]" testid="stat-clicked" />
          <Stat label="Open rate" value={`${data.open_rate}%`} color="text-[#00F0FF]" testid="stat-open-rate" />
          <Stat label="Click rate" value={`${data.click_rate}%`} color="text-[#00FF94]" testid="stat-click-rate" />
        </div>

        {/* Controls */}
        <div className="flex items-center gap-3 flex-wrap rounded-xl border border-white/10 bg-white/[0.03] p-4">
          <Filter className="h-4 w-4 text-white/40" />
          <span className="text-xs uppercase tracking-wider font-bold text-white/60">Status</span>
          <div className="flex gap-1.5">
            {STATUS_OPTIONS.map((o) => (
              <button
                key={o.value} type="button"
                data-testid={`status-${o.value}-btn`}
                onClick={() => setStatusFilter(o.value)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                  statusFilter === o.value
                    ? 'bg-[#00F0FF] text-black'
                    : 'bg-white/5 text-white/70 hover:bg-white/10'
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
          <input
            type="search" value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search partner, email, or subject…"
            data-testid="outreach-log-search"
            className="ml-auto w-60 bg-black/40 border border-white/15 rounded-lg px-3 py-1.5 text-sm text-white placeholder:text-white/30"
          />
        </div>

        {/* Log table */}
        {loading && data.items.length === 0 ? (
          <div className="rounded-xl border border-white/10 p-8 text-center text-white/50">
            <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" /> Loading…
          </div>
        ) : visible.length === 0 ? (
          <EmptyState statusFilter={statusFilter} />
        ) : (
          <div className="rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden" data-testid="outreach-log-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-white/5 text-xs uppercase tracking-wider text-white/60">
                  <tr>
                    <th className="text-left py-2.5 px-3 font-semibold">Partner</th>
                    <th className="text-left py-2.5 px-3 font-semibold">Recipient</th>
                    <th className="text-left py-2.5 px-3 font-semibold">Subject</th>
                    <th className="text-center py-2.5 px-3 font-semibold">Status</th>
                    <th className="text-center py-2.5 px-3 font-semibold">Engagement</th>
                    <th className="text-right py-2.5 px-3 font-semibold">Sent</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {visible.map((it) => (
                    <LogRow key={it.id} item={it} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function Stat({ label, value, sub, icon: Icon, color = 'text-white', testid }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider font-bold text-white/60">
        {Icon && <Icon className="h-3 w-3" />}
        {label}
      </div>
      <div className={`text-2xl font-bold mt-1 truncate ${color}`}>{value}</div>
      {sub && <div className="text-xs text-white/50 mt-0.5">{sub}</div>}
    </div>
  );
}

function EmptyState({ statusFilter }) {
  return (
    <div className="rounded-xl border border-dashed border-white/15 p-10 text-center" data-testid="empty-state">
      <Inbox className="h-8 w-8 text-white/40 mx-auto mb-3" />
      <p className="text-sm text-white/70 mb-1">No outreach log entries yet.</p>
      <p className="text-xs text-white/50">
        {statusFilter === 'all'
          ? 'Send your first pitch from /admin/upgrade-candidates and it\'ll appear here.'
          : `No entries match status "${statusFilter}". Try "All" filter.`}
      </p>
    </div>
  );
}

function LogRow({ item }) {
  const it = item;
  const opened = it.open_count > 0;
  const clicked = it.click_count > 0;
  const bounced = it.bounce_count > 0;
  const complained = it.complaint_count > 0;

  return (
    <tr className="hover:bg-white/[0.02]" data-testid={`outreach-log-row-${it.id}`}>
      {/* Partner */}
      <td className="py-2.5 px-3">
        <div className="flex items-center gap-2 min-w-0">
          {it.partner_logo ? (
            <img
              src={it.partner_logo} alt={it.partner_name}
              className="w-7 h-7 rounded object-contain bg-white/5 border border-white/10 p-0.5 flex-shrink-0"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : (
            <div className="w-7 h-7 rounded bg-white/5 border border-white/10 flex-shrink-0" />
          )}
          <div className="min-w-0">
            <div className="font-bold text-white text-sm truncate">{it.partner_name}</div>
            {it.partner_category && (
              <div className="text-[10px] uppercase tracking-wider text-white/40 truncate">
                {it.partner_category}
              </div>
            )}
          </div>
        </div>
      </td>
      {/* Recipient */}
      <td className="py-2.5 px-3 text-white/80 font-mono text-xs truncate max-w-[220px]" title={it.to_email}>
        {it.to_email}
      </td>
      {/* Subject */}
      <td className="py-2.5 px-3">
        <div className="text-white/80 text-xs truncate max-w-[280px]" title={it.subject}>
          {it.subject}
          {it.subject_variant && (
            <span className="ml-2 text-[10px] uppercase tracking-wider text-white/40 font-bold">
              · {it.subject_variant.toUpperCase()}
            </span>
          )}
        </div>
      </td>
      {/* Status */}
      <td className="py-2.5 px-3 text-center">
        {it.status === 'sent' && (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-[#00FF94]/15 text-[#00FF94] border border-[#00FF94]/30">
            <CheckCircle2 className="h-3 w-3" />Sent
          </span>
        )}
        {it.status === 'failed' && (
          <span
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-red-500/15 text-red-300 border border-red-500/30"
            title={it.error || 'Send failed'}
          >
            <XCircle className="h-3 w-3" />Failed
          </span>
        )}
        {bounced && (
          <span
            className="ml-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30"
            title="Bounced"
          >
            <AlertTriangle className="h-3 w-3" />Bounce
          </span>
        )}
        {complained && (
          <span
            className="ml-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-red-500/15 text-red-300 border border-red-500/30"
            title="Spam complaint"
          >
            <AlertTriangle className="h-3 w-3" />Spam
          </span>
        )}
      </td>
      {/* Engagement */}
      <td className="py-2.5 px-3 text-center">
        <div className="inline-flex items-center gap-2 text-xs">
          <span
            className={`inline-flex items-center gap-1 ${opened ? 'text-[#00F0FF]' : 'text-white/30'}`}
            title={opened ? `Opened ${it.open_count}× · last ${relativeTime(it.last_opened_at)}` : 'Not opened yet'}
          >
            <Eye className="h-3 w-3" />{it.open_count}
          </span>
          <span
            className={`inline-flex items-center gap-1 ${clicked ? 'text-[#00FF94]' : 'text-white/30'}`}
            title={clicked ? `Clicked ${it.click_count}× · last ${relativeTime(it.last_clicked_at)}` : 'No clicks yet'}
          >
            <MousePointerClick className="h-3 w-3" />{it.click_count}
          </span>
        </div>
      </td>
      {/* Sent timestamp */}
      <td className="py-2.5 px-3 text-right text-xs text-white/60 whitespace-nowrap">
        <span className="inline-flex items-center gap-1" title={it.sent_at}>
          <Clock className="h-3 w-3 text-white/40" />
          {relativeTime(it.sent_at)}
        </span>
      </td>
    </tr>
  );
}

export default AdminOutreachLog;
